import { Marked, Renderer, type Tokens } from 'marked';

import { escapeHtml } from '@core/utils/html-escape.utils';

export interface AssistantMarkdownOptions {
  /** Footer label for a table long enough to scroll; no footer when absent. */
  rowCount?: (rows: number) => string;
  /** Label of a code block's Copy control; no control when absent. */
  copyLabel?: () => string;
}

// A table this long overflows the frame's max height, so its size is no longer visible at a glance.
const ROW_COUNT_FROM = 10;

// One quantity with an optional sign, currency and unit: `318.4 km`, `€ 1 204.10`, `+4.2 %`, `42 min`.
// A space inside a number only ever separates a group of three digits.
const QUANTITY = String.raw`[+\-−]?\s?(?:[€$£]\s?)?\d[\d.,']*(?:\s\d{3}[\d.,']*)*(?:\s?(?:%|[A-Za-zµ°€$£/]{1,5}))?`;
// One or more quantities, so compact durations such as `1 h 03 m` read as one number.
const NUMERIC = new RegExp(`^${QUANTITY}(?:\\s+${QUANTITY})*$`);
// A number cell is short. The cap also bounds the regex's backtracking on a long run of numbers that
// fails to match, which otherwise grows exponentially and runs again on every streamed chunk.
const MAX_NUMBER_CELL = 32;
const GAP = /^[—–-]?$/;

function isGap(cell: Tokens.TableCell): boolean {
  return GAP.test(cell.text.trim());
}

// A cell in backticks is an id such as `R03333`, never a number to align.
function readsAsNumber(cell: Tokens.TableCell): boolean {
  const text = cell.text.trim();
  return (
    text.length <= MAX_NUMBER_CELL &&
    !cell.tokens.some((t) => t.type === 'codespan') &&
    NUMERIC.test(text)
  );
}

function numericColumns(token: Tokens.Table): boolean[] {
  return token.header.map((_, i) => {
    const filled = token.rows.map((row) => row[i]).filter((c) => c && !isGap(c));
    return filled.length > 0 && filled.every(readsAsNumber);
  });
}

// A column whose longest cell is short stays on one line and the table scrolls sideways; squeezing it into
// the 400 px panel would break ids and names over two lines.
const SHORT_CELL = 32;

function longColumns(token: Tokens.Table): boolean[] {
  return token.header.map((header, i) =>
    [header, ...token.rows.map((row) => row[i])].some(
      (c) => c && c.text.length > SHORT_CELL
    )
  );
}

const ALIGN = { left: 'text-left', center: 'text-center', right: 'text-right' } as const;
const FROZEN = 'sticky left-0 border-r border-weak';
const NUMBER = 'num text-right font-mono tabular-nums whitespace-nowrap';

function renderTable(
  this: Renderer,
  token: Tokens.Table,
  options: AssistantMarkdownOptions
): string {
  const numeric = numericColumns(token);
  const long = longColumns(token);
  // The model's explicit alignment wins over detection.
  const cellClass = (i: number) => {
    const align = token.align[i];
    const wrap = long[i] ? 'min-w-[14rem]' : 'whitespace-nowrap';
    if (align) return `${ALIGN[align]} ${wrap}`;
    return numeric[i] ? NUMBER : `text-left ${wrap}`;
  };
  const inline = (cell: Tokens.TableCell) => this.parser.parseInline(cell.tokens);
  const head = token.header
    .map(
      (cell, i) =>
        `<th scope="col" class="bg-surface border-default text-text-secondary sticky top-0 border-b px-2.5 py-1.5 text-2xs font-medium whitespace-nowrap ${
          i === 0 ? `${FROZEN} z-[2]` : 'z-[1]'
        } ${cellClass(i)}">${inline(cell)}</th>`
    )
    .join('');
  const body = token.rows
    .map(
      (row) =>
        `<tr class="group">${row
          .map(
            (cell, i) =>
              `<td class="border-weak group-hover:bg-interactive border-b px-2.5 py-1 align-top ${
                i === 0 ? `bg-elevated ${FROZEN}` : ''
              } ${cellClass(i)}">${inline(cell)}</td>`
          )
          .join('')}</tr>`
    )
    .join('');
  const rows = token.rows.length;
  const footer =
    options.rowCount && rows >= ROW_COUNT_FROM
      ? `<div class="assistant-table-rows bg-surface border-default text-text-tertiary border-t px-2.5 py-1 text-2xs">${escapeHtml(options.rowCount(rows))}</div>`
      : '';
  return (
    `<div class="assistant-table not-prose border-default my-3 overflow-hidden rounded-md border">` +
    `<div class="max-h-[18rem] overflow-auto">` +
    `<table class="text-text-primary w-full border-separate border-spacing-0 text-xs leading-snug">` +
    `<thead><tr>${head}</tr></thead><tbody>${body}</tbody></table></div>${footer}</div>`
  );
}

function renderCode(token: Tokens.Code, options: AssistantMarkdownOptions): string {
  const lang = (token.lang ?? '').trim().split(/\s+/)[0] ?? '';
  // A span, not a button: Angular's sanitizer drops <button> from [innerHTML] but keeps role and tabindex.
  const copy = options.copyLabel
    ? `<span role="button" tabindex="0" class="assistant-copy-code text-text-secondary hover:text-text-primary hover:bg-interactive inline-flex cursor-pointer items-center gap-1 rounded-sm px-1.5 py-0.5 text-2xs font-medium transition-colors"><i class="ph-bold ph-copy"></i><span class="assistant-copy-label">${escapeHtml(options.copyLabel())}</span></span>`
    : '';
  const head =
    lang || copy
      ? `<div class="border-weak flex items-center justify-between gap-2 border-b py-1 pr-1 pl-3"><span class="text-text-tertiary font-mono text-2xs">${escapeHtml(lang)}</span>${copy}</div>`
      : '';
  return (
    `<div class="assistant-code not-prose bg-page border-default my-3 overflow-hidden rounded-md border">${head}` +
    `<pre class="overflow-x-auto px-3 py-2 text-xs leading-relaxed"><code class="text-text-primary font-mono">${escapeHtml(token.text)}</code></pre></div>`
  );
}

export function createAssistantMarkdown(options: AssistantMarkdownOptions = {}): Marked {
  return new Marked({
    gfm: true,
    async: false,
    renderer: {
      // Images and raw HTML are shown as text: an <img> would make the browser fetch a model-chosen URL
      // without a click, and a raw <a> would skip the link renderer below.
      image: ({ text }: Tokens.Image) => escapeHtml(text),
      html: ({ text }: Tokens.HTML | Tokens.Tag) => escapeHtml(text),
      // A plain link would navigate the GFE tab away from the map the user is asking about.
      link(this: Renderer, token: Tokens.Link) {
        return Renderer.prototype.link
          .call(this, token)
          .replace(/^<a /, '<a target="_blank" rel="noopener noreferrer" ');
      },
      table(this: Renderer, token: Tokens.Table) {
        return renderTable.call(this, token, options);
      },
      code(token: Tokens.Code) {
        return renderCode(token, options);
      }
    }
  });
}

export const assistantMarkdown = createAssistantMarkdown();
